import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Star, X, Save, Search, Mail, Phone, Tag } from "lucide-react";
import { adminListGuests, adminUpdateGuest } from "../../lib/api";

export default function GuestsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | repeat | vip | paying
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows(await adminListGuests()); }
    catch { toast.error("Could not load guests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((g) => {
      if (filter === "repeat" && !g.is_repeat) return false;
      if (filter === "vip" && !g.vip) return false;
      if (filter === "paying" && g.paid_stays === 0) return false;
      if (needle) {
        const hay = `${g.full_name || ""} ${g.email} ${g.phone || ""} ${(g.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, filter]);

  const counts = {
    all: rows.length,
    repeat: rows.filter((g) => g.is_repeat).length,
    vip: rows.filter((g) => g.vip).length,
    paying: rows.filter((g) => g.paid_stays > 0).length,
  };

  return (
    <div data-testid="guests-panel">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="ed-overline mb-2">Operations</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Guests</h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">{rows.length} unique guest{rows.length === 1 ? "" : "s"} · auto-deduped from bookings · tag + note your VIPs.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ed-ink-muted)]" />
          <input
            data-testid="guests-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone, tag…"
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] outline-none rounded-sm"
          />
        </div>
      </header>

      <div className="flex flex-wrap gap-2 mb-6 pb-5 border-b border-[var(--ed-rule)]">
        {[
          ["all", `All · ${counts.all}`],
          ["paying", `Paying · ${counts.paying}`],
          ["repeat", `Repeat · ${counts.repeat}`],
          ["vip", `VIP · ${counts.vip}`],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`guests-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2 border rounded-sm transition-colors ${
              filter === k
                ? "bg-[var(--ed-ink)] text-[var(--ed-bg)] border-[var(--ed-ink)]"
                : "border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:border-[var(--ed-ink)] hover:text-[var(--ed-ink)]"
            }`}
          >{l}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--ed-rule)] rounded-sm p-16 text-center">
          <p className="text-[var(--ed-ink-muted)]">No guests match this view yet.</p>
        </div>
      ) : (
        <div className="border border-[var(--ed-rule)] rounded-sm overflow-x-auto bg-[var(--ed-bg)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] text-[0.6rem] uppercase tracking-[0.22em]">
              <tr>
                <th className="text-left px-5 py-3">Guest</th>
                <th className="text-left px-5 py-3">Stays</th>
                <th className="text-left px-5 py-3">Lifetime value</th>
                <th className="text-left px-5 py-3">Last stay</th>
                <th className="text-left px-5 py-3">Tags</th>
                <th className="text-left px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.email} className="border-t border-[var(--ed-rule)] text-[var(--ed-ink-soft)]" data-testid={`guest-row-${g.email}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      {g.vip && <Star size={12} className="text-[var(--ed-emerald)] flex-shrink-0" fill="currentColor" />}
                      <p className="text-[var(--ed-ink)] truncate">{g.full_name || "(unnamed)"}</p>
                    </div>
                    <a href={`mailto:${g.email}`} className="text-[var(--ed-ink-muted)] text-xs hover:text-[var(--ed-emerald)] flex items-center gap-1 mt-0.5">
                      <Mail size={10} /> {g.email}
                    </a>
                    {g.phone && (
                      <a href={`tel:${g.phone}`} className="text-[var(--ed-ink-muted)] text-xs hover:text-[var(--ed-emerald)] flex items-center gap-1 mt-0.5">
                        <Phone size={10} /> {g.phone}
                      </a>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-[var(--ed-ink)]">{g.stays} <span className="text-[var(--ed-ink-muted)] text-xs">total</span></p>
                    <p className="text-[var(--ed-emerald)] text-xs">{g.paid_stays} paid</p>
                    {g.is_repeat && <span className="inline-block mt-1 text-[0.55rem] uppercase tracking-widest bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] px-2 py-0.5 rounded-sm">Repeat</span>}
                  </td>
                  <td className="px-5 py-3.5 font-serif text-lg text-[var(--ed-ink)]">£{Number(g.lifetime_value).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</td>
                  <td className="px-5 py-3.5 text-xs whitespace-nowrap">{g.last_stay || "—"}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {(g.tags || []).slice(0, 4).map((t) => (
                        <span key={t} className="text-[0.55rem] uppercase tracking-widest bg-[var(--ed-paper)] border border-[var(--ed-rule)] text-[var(--ed-ink-soft)] px-2 py-0.5 rounded-sm">{t}</span>
                      ))}
                      {(g.tags || []).length === 0 && <span className="text-[var(--ed-ink-muted)] text-xs">—</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      data-testid={`guest-edit-${g.email}`}
                      onClick={() => setEditing(g)}
                      className="text-[0.6rem] uppercase tracking-[0.22em] px-3 py-2 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] rounded-sm flex items-center gap-1.5 ml-auto"
                    >
                      <Tag size={11} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <GuestEditModal
          guest={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); setEditing(null); }}
        />
      )}
    </div>
  );
}

function GuestEditModal({ guest, onClose, onSaved }) {
  const [tagsInput, setTagsInput] = useState((guest.tags || []).join(", "));
  const [notes, setNotes] = useState(guest.notes || "");
  const [vip, setVip] = useState(!!guest.vip);
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      const tags = tagsInput.split(",").map((s) => s.trim()).filter(Boolean);
      await adminUpdateGuest(guest.email, { tags, notes, vip });
      toast.success("Guest updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div data-testid="guest-edit-modal" className="w-full max-w-lg my-8 bg-[var(--ed-bg)] border border-[var(--ed-rule)] rounded-sm shadow-2xl">
        <div className="px-7 pt-6 pb-5 border-b border-[var(--ed-rule)] flex items-start justify-between gap-4">
          <div>
            <p className="ed-overline mb-1">Guest</p>
            <h2 className="font-serif text-2xl text-[var(--ed-ink)]">{guest.full_name || "(unnamed)"}</h2>
            <p className="text-[var(--ed-ink-muted)] text-sm mt-1">{guest.email}</p>
          </div>
          <button onClick={onClose} className="text-[var(--ed-ink-muted)] hover:text-[var(--ed-ink)] p-1"><X size={20} /></button>
        </div>
        <div className="p-7 space-y-5">
          <label className="flex items-center gap-2 text-sm text-[var(--ed-ink-soft)] cursor-pointer">
            <input data-testid="guest-vip" type="checkbox" checked={vip} onChange={(e) => setVip(e.target.checked)} className="w-4 h-4 accent-[var(--ed-emerald)]" />
            <Star size={13} className={vip ? "text-[var(--ed-emerald)]" : "text-[var(--ed-ink-muted)]"} fill={vip ? "currentColor" : "none"} />
            <span>VIP guest</span>
          </label>
          <div>
            <label className="block text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-1.5">
              Tags <span className="normal-case tracking-normal">(comma separated)</span>
            </label>
            <input
              data-testid="guest-tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Corporate, Quiet, Late check-in"
              className="w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm"
            />
          </div>
          <div>
            <label className="block text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-1.5">Internal notes</label>
            <textarea
              data-testid="guest-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Allergies, preferences, anything the concierge should remember."
              className="w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm resize-none"
            />
          </div>

          <div className="border-t border-[var(--ed-rule)] pt-4 text-xs text-[var(--ed-ink-muted)] grid grid-cols-2 gap-y-1.5">
            <span>Total stays</span><span className="text-right text-[var(--ed-ink)]">{guest.stays}</span>
            <span>Paid stays</span><span className="text-right text-[var(--ed-emerald)]">{guest.paid_stays}</span>
            <span>Lifetime value</span><span className="text-right text-[var(--ed-ink)]">£{Number(guest.lifetime_value).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</span>
            <span>First stay</span><span className="text-right text-[var(--ed-ink)]">{guest.first_stay || "—"}</span>
            <span>Last stay</span><span className="text-right text-[var(--ed-ink)]">{guest.last_stay || "—"}</span>
            <span>Properties</span><span className="text-right text-[var(--ed-ink)] truncate" title={(guest.properties || []).join(", ")}>{(guest.properties || []).join(", ") || "—"}</span>
          </div>
        </div>
        <div className="px-7 py-5 border-t border-[var(--ed-rule)] bg-[var(--ed-paper)]/40 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2.5 border border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:border-[var(--ed-ink)] hover:text-[var(--ed-ink)] rounded-sm">Cancel</button>
          <button data-testid="guest-save" onClick={onSave} disabled={saving} className="text-[0.65rem] uppercase tracking-[0.22em] px-5 py-2.5 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm flex items-center gap-2 disabled:opacity-50">
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
